import User from "@bevco/MobiusOverrides/Icons/User";
import getAccountManagerInfo from "@bevco/Store/Handlers/MyAccount/GetAccountManager";
import getCustomerManagerInfo from "@bevco/Store/Handlers/MyAccount/GetCustomerManager";
import { colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Phone from "@insite/mobius/Icons/Phone";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        billtoId: state.context.session.billToId,
    };
};

const mapDispatchToProps = {
    getAccountManagerInfo,
    getCustomerManagerInfo,
};

export type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface AccountManagerStyles {
    title?: TypographyProps;
    container?: GridContainerProps;
    innerWrapper?: GridItemProps;
    imageWrapper?: InjectableCss;
    name?: TypographyProps;
    accountManagerTitle?: TypographyProps;
    customerManagerTitle?: TypographyProps;
    icon?: IconProps;
    link?: LinkPresentationProps;
}

export const accountManagerStyles: AccountManagerStyles = {
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
        `,
    },
    accountManagerTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 8px;
        `,
    },
    customerManagerTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 8px;
        `,
    },
    container: {
        gap: 10,
        offsetCss: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 30px;
        `,
    },

    innerWrapper: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            padding: 1rem;
            max-width: 260px;

            ${mediaQueriesBP.maxMD} {
                max-width: 100%;
            }
        `,
    },

    imageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-bottom: 20px;
            height: 120px;
            width: 120px;
            border-radius: 8px;
            overflow: hidden;

            img {
                width: 100%;
                height: auto;
            }
        `,
    },

    icon: {
        css: css`
            margin-right: 8px;
        `,
    },

    name: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 8px;
        `,
    },

    link: {
        css: css`
            margin-bottom: 8px;
            span {
                display: flex;
                align-items: center;
            }
        `,
    },
};
export const styles = accountManagerStyles;

export const AccountManagerWidget = ({ getAccountManagerInfo, getCustomerManagerInfo, billtoId, fields }: Props) => {
    const [accountManagerInfo, setAccountManagerInfo] = useState({}) as any;
    const [customerManagerInfo, setCustomerManagerInfo] = useState({}) as any;

    useEffect(() => {
        if (!billtoId) {
            return;
        }

        getAccountManagerInfo({
            customerId: billtoId || "",
            onSuccess: result => {},
            onComplete: result => {
                if (result.apiResult) {
                    setAccountManagerInfo(result.apiResult.SalesPerson);
                }
            },
        });

        getCustomerManagerInfo({
            customerId: billtoId || "",
            onSuccess: result => {},
            onComplete: result => {
                if (result.apiResult) {
                    setCustomerManagerInfo(result.apiResult.SalesPerson);
                }
            },
        });
    }, [billtoId]);

    if (!accountManagerInfo || !customerManagerInfo) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{fields.title}</Typography>
            <GridContainer {...styles.container}>
                {accountManagerInfo && accountManagerInfo.Name && accountManagerInfo.Email && (
                    <GridItem {...styles.innerWrapper}>
                        {accountManagerInfo.ImagePath && (
                            <StyledWrapper {...styles.imageWrapper}>
                                <Img src={accountManagerInfo.ImagePath} />
                            </StyledWrapper>
                        )}
                        {accountManagerInfo.Title && (
                            <Typography {...styles.accountManagerTitle}>{accountManagerInfo.Title}</Typography>
                        )}
                        {accountManagerInfo.Name && (
                            <Typography {...styles.name}>
                                <Icon {...styles.icon} src={User} />
                                {accountManagerInfo.Name}
                            </Typography>
                        )}
                        {accountManagerInfo.Email && (
                            <Link {...styles.link} href={`mailto:${accountManagerInfo.Email}`}>
                                <Icon {...styles.icon} src={Mail} />
                                {accountManagerInfo.Email}
                            </Link>
                        )}
                        {accountManagerInfo.Phone1 && (
                            <Link {...styles.link} href={`tel:${accountManagerInfo.Phone1}`}>
                                <Icon {...styles.icon} src={Phone} />
                                {accountManagerInfo.Phone1}
                            </Link>
                        )}
                        {accountManagerInfo.Phone2 && (
                            <Link {...styles.link} href={`tel:${accountManagerInfo.Phone2}`}>
                                <Icon {...styles.icon} src={Phone} />
                                {accountManagerInfo.Phone2}
                            </Link>
                        )}
                    </GridItem>
                )}
                {customerManagerInfo && customerManagerInfo.Name && customerManagerInfo.Email && (
                    <GridItem {...styles.innerWrapper}>
                        {customerManagerInfo.ImagePath && (
                            <StyledWrapper {...styles.imageWrapper}>
                                <Img src={customerManagerInfo.ImagePath} />
                            </StyledWrapper>
                        )}
                        {customerManagerInfo.Title && (
                            <Typography {...styles.customerManagerTitle}>{customerManagerInfo.Title}</Typography>
                        )}
                        {customerManagerInfo.Name && (
                            <Typography {...styles.name}>
                                <Icon {...styles.icon} src={User} />
                                {customerManagerInfo.Name}
                            </Typography>
                        )}
                        {customerManagerInfo.Email && (
                            <Link {...styles.link} href={`mailto:${customerManagerInfo.Email}`}>
                                <Icon {...styles.icon} src={Mail} />
                                {customerManagerInfo.Email}
                            </Link>
                        )}
                    </GridItem>
                )}
            </GridContainer>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountManagerWidget as any) as any,
    definition: {
        displayName: "Account Manager",
        group: "Verndale Box" as any,
        icon: "puzzle-piece",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "My Account Manager",
                fieldType: "Translatable",
                sortOrder: 1,
                isRequired: true,
            },
        ],
    },
};
export default widgetModule;
